import React, { useEffect, useState } from 'react';
import './styles.less';
import * as yup from 'yup';
import { typeToFormErrors } from 'utils/validate';
import { useGlobal } from 'context';
import { request } from 'utils/api';
import { PUBLIC_TOKEN } from 'utils/env';
import { withLayout } from 'helpers';
import { getLandingPageVariantString } from 'screens/TalkToUs/utils';
import { useFormVariant } from 'components/FeaturesContext';
import MessagingPolicy from 'screens/SignupFlows/steps/MessagingPolicy';
import { useTracker } from 'utils/tracking';
import sideImage from 'assets/ttu/ttu-step-4.avif';
import { InputV2 } from 'components/InputV2';
import { PhoneNumberInputV2 } from 'components/PhoneNumberInput';
import { GenericQuestionContainer } from '../components/GenericQuestionContainer';

const schema = yup.object().shape({
  firstName: yup
    .string()
    .min(2, 'First name is too short')
    .required('First name is required'),
  lastName: yup
    .string()
    .min(2, 'Last name is too short')
    .required('Last name is required'),
  email: yup.string().email('Email is not valid').required('Email is required'),
  textMessageOptIn: yup.boolean().oneOf([true], 'This field must be checked'),
  phone: yup
    .string()
    .matches(
      /^(?!(0))^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
      {
        message: 'Must be a valid US phone number.',
        excludeEmptyString: true,
      }
    )
    .min(10, 'Phone number is too short')
    .max(10, 'Phone number is too long'),
});

function YourEmail(props) {
  const context = useGlobal();
  const trackOnce = useTracker();
  const _formVariant = useFormVariant();

  const [firstName, setFirstName] = useState(context.account?.firstName || '');
  const [lastName, setLastName] = useState(context.account?.lastName || '');
  const [email, setEmail] = useState(context.account?.email || '');

  const [phone, setPhone] = useState(context.account.phone || '');
  const [textMessageOptIn, setTextMessageOptIn] = useState(false);

  const [fieldsErrors, setFieldsErrors] = useState({});

  const canContinue =
    firstName && lastName && email && phone.length == 10 && textMessageOptIn;

  useEffect(() => context.setLeadQuestions([]), []);

  async function onClickNext() {
    const basicInfo = { firstName, lastName, email, phone };

    try {
      await schema.validate(basicInfo, { abortEarly: false });
    } catch (e) {
      setFieldsErrors(typeToFormErrors(e));
      return;
    }

    context.setAccount({ ...context.account, ...basicInfo });

    trackOnce('lead_question5_email');
    props.history.push(props.nextRoute);

    const requestBody = {
      ...context.startInfo,
      acceptedTerms: false,
      createLead: props.createLead || false,
      email,
      entry_origin: window.localStorage.entry_origin,
      firstName,
      flowCreatesLead: !props.redirectsToSchedule,
      formVariant: _formVariant,
      landingPageVariant: getLandingPageVariantString(
        window.localStorage.form_variant,
        window.localStorage.variant
      ),
      lastName,
      phone,
      practiceCode: window.sessionStorage.getItem('practiceCode') || 'nuelife',
      recaptchaToken: 'empty',
      textMessageOptIn,
      trafficType: window.localStorage.traffic_source,
      utmCampaign: window.localStorage.utm_campaign,
      utmContent: window.localStorage.utm_content,
      utmMedium: window.localStorage.utm_medium,
      utmSource: window.localStorage.utm_source,
      utmTerm: window.localStorage.utm_term,
    };

    await request({
      method: 'POST',
      path: '/v1/web/auth/pre-sign-up',
      body: requestBody,
      headers: { Authorization: `Basic ${PUBLIC_TOKEN}` },
    });

    window?.gtag('event', 'submitted_pre_account_form');
  }

  return (
    <GenericQuestionContainer
      onBack={() => props.history.push(props.prevRoute)}
      onNext={onClickNext}
      currentStep={1}
      totalSteps={10}
      questionTitle={
        'Where can we send wellness information about the Nue Life program?'
      }
      disableNext={!canContinue}
      sideImage={sideImage}
      whiteBackground
      footer={
        <>
          <MessagingPolicy
            hasTextMessageOptIn={textMessageOptIn}
            setField={(e) => setTextMessageOptIn(e)}
            fieldsErrors={fieldsErrors}
          />
        </>
      }>
      {/* First Name */}
      <div>
        <InputV2
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          label="First Name"
          error={fieldsErrors.firstName}
        />
      </div>

      {/* Last Name */}
      <div>
        <InputV2
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          label="Last Name"
          error={fieldsErrors.lastName}
        />
      </div>

      {/* Email */}
      <div>
        <InputV2
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          label="Email Address"
          error={fieldsErrors.email}
        />
      </div>

      {/* Phone number */}
      <div>
        <PhoneNumberInputV2
          value={phone}
          onChange={({ value }) => setPhone(value)}
          label="Phone number"
          error={fieldsErrors.phone}
          type="tel"
        />
      </div>
    </GenericQuestionContainer>
  );
}

YourEmail.title = 'Question 1 | Nue Life Questionnaire';

export default withLayout(YourEmail);